/**
 * @fileoverview Route guard component for protected routes.
 * Redirects unauthenticated users to the login page.
 * Shows AccessDenied page if user lacks required roles.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../pages/AccessDenied';

/**
 * ProtectedRoute wraps child components with authentication and role checks.
 *
 * @param {Object} props
 * @param {string[]} [props.requiredRoles] - Roles required to access this route (e.g., ['admin', 'super_admin'])
 * @param {React.ReactNode} props.children - The route content to render if authorized
 * @returns {JSX.Element|null} Children if authorized, AccessDenied if wrong role, null if loading
 *
 * @example
 * <ProtectedRoute requiredRoles={['admin']}>
 *   <AdminPage />
 * </ProtectedRoute>
 */
function ProtectedRoute({ requiredRoles = null, children }) {
  const { initialized, authenticated, activeOrg, isSuperAdmin } = useAuth();

  // Still loading auth state
  if (!initialized) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Not authenticated — redirect to login page
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = activeOrg?.role;
    const hasRole = isSuperAdmin || (userRole && requiredRoles.includes(userRole));
    if (!hasRole) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
}

ProtectedRoute.propTypes = {
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
